import React, { useCallback, useEffect, useRef, useState } from 'react';

import LeadMessagesSurface from 'src/pages/landlord/leads/messages/LeadMessagesSurface';
import DrawerLeadMessagesModeProvider from 'src/pages/landlord/conversations/components/drawer/DrawerLeadMessagesModeProvider';

import styles from 'src/pages/landlord/conversations/components/drawer/ConversationDrawer.module.css';

type Props = {
    open: boolean;
    onClose: () => void;
};

// --- Body scroll lock -------------------------------------------------------------------
// Reentrant-safe via a module-level lock count, so a MUI Modal/Dialog opened concurrently
// (e.g. from within the drawer's content) doesn't stomp this lock's saved restore values, and
// vice versa: only the first locker applies styles, only the last locker restores them.
let scrollLockCount = 0;
let savedBodyOverflow = '';
let savedBodyPaddingRight = '';

function lockBodyScroll() {
    if (scrollLockCount === 0) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        savedBodyOverflow = document.body.style.overflow;
        savedBodyPaddingRight = document.body.style.paddingRight;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            const currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
            document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
        }
    }
    scrollLockCount += 1;
}

function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
        document.body.style.overflow = savedBodyOverflow;
        document.body.style.paddingRight = savedBodyPaddingRight;
    }
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const prefersReducedMotion = () => (
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
);

/**
 * Right-side slide-in drawer shell hosting the lead-messages conversation over the
 * inbox/search list on large screens. Shell only: no wiring into the inbox/search pages here.
 */
export default function ConversationDrawer({ open, onClose }: Props) {
    const panelRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

    // Keep the panel/backdrop mounted through the closing transition (so it can slide out),
    // but stop rendering `LeadMessagesSurface` once fully closed, so its polling/queries don't
    // keep running while hidden.
    const [hasOpenedOnce, setHasOpenedOnce] = useState(open);
    const [shouldRenderContent, setShouldRenderContent] = useState(open);

    useEffect(() => {
        if (open) {
            setHasOpenedOnce(true);
            setShouldRenderContent(true);
            return;
        }

        // With `prefers-reduced-motion: reduce` the CSS transition is disabled, so
        // `onTransitionEnd` never fires — stop rendering the content immediately instead of
        // waiting for a transition that won't happen.
        if (prefersReducedMotion()) {
            setShouldRenderContent(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        lockBodyScroll();
        return () => unlockBodyScroll();
    }, [open]);

    useEffect(() => {
        if (open) {
            previouslyFocusedElementRef.current = document.activeElement as HTMLElement | null;
            panelRef.current?.focus();
        } else if (previouslyFocusedElementRef.current) {
            previouslyFocusedElementRef.current.focus();
            previouslyFocusedElementRef.current = null;
        }
    }, [open]);

    // Focus trap: cycle Tab/Shift+Tab within the panel while open.
    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || !panelRef.current) {
                return;
            }

            const focusableElements = Array.from(
                panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
            ).filter((element) => element.offsetParent !== null);

            if (focusableElements.length === 0) {
                event.preventDefault();
                return;
            }

            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    const handlePanelTransitionEnd = useCallback((event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
            return;
        }
        if (!open) {
            setShouldRenderContent(false);
        }
    }, [open]);

    if (!hasOpenedOnce) {
        return null;
    }

    return (
        <>
            <div
                className={`${styles.backdrop} ${open ? styles.backdropOpen : ''}`}
                onClick={onClose}
                data-testid="conversation-drawer-backdrop"
            />
            <div
                ref={panelRef}
                className={`${styles.panel} ${open ? styles.panelOpen : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Conversation"
                tabIndex={-1}
                onTransitionEnd={handlePanelTransitionEnd}
                data-testid="conversation-drawer"
            >
                {shouldRenderContent && (
                    <DrawerLeadMessagesModeProvider>
                        <LeadMessagesSurface />
                    </DrawerLeadMessagesModeProvider>
                )}
            </div>
        </>
    );
}
